import { useState, useEffect } from 'react';
import BlacklistWarningItem from '../warnings/transaction-blacklist-warning';
import ButtonPseudo from '../../button/button-pseudo';
import formatDate from '../../../helpers/format-date';
import IconDown from '../../icons/down';
import IconExpandAll from '../../icons/expand-all';
import IconHideAll from '../../icons/hide-all';
import IconUp from '../../icons/up';
import { BlacklistWarningGroupTypes } from './types';

import cn from './styles.module.css';

export default function BlacklistWarningGroup({
  group,
  currentEmployee,
  resetPaymentOrderTransactionActionIsLoading,
  activePermissions,
  setBlacklistDataItem,
  setBlacklistData,
  applyWarningInProgress,
  onResetTransactionWarningAction,
  applyWarning,
  setBlacklistDataVisible,
  setPanelIsOpen,
  setBlacklistItemVisible,
  resetPaymentOrderTransactionActionIsSuccess,
  selectedWarningId,
  getPaymentOrderWarningsAction,
  paginationInProgress,
  selectedWarningGroup,
  getPaymentOrderWarningById,
  isWarningByIdLoading,
  isWarningByIdFetching,
  warningsHidden,
  showPassed,
  hiddenWarningGroupsIds,
  setHiddenWarningGroupsIds,
  warningsTotal,
  hideApproveActions,
  hideRejectActions,
}: BlacklistWarningGroupTypes) {
  const [collapsed, setCollapsed] = useState(true);
  const [hideWarnings, setHideWarnings] = useState(false);
  const [hideWarningsIds, setHideWarningsIds] = useState<string[]>([]);

  useEffect(() => {
    setHideWarnings(warningsHidden as boolean);

    let nextHiddenWarningGroups = ([] as string[]).concat(
      hiddenWarningGroupsIds as string[],
    );

    if (warningsHidden) {
      setHideWarningsIds(
        group?.warnings?.edges?.map((item) => item.node.id) as string[],
      );

      nextHiddenWarningGroups.push(group?.id as string);
    } else {
      setHideWarningsIds([]);

      nextHiddenWarningGroups = nextHiddenWarningGroups?.filter(
        (item) => item !== group?.id,
      );
    }

    if (setHiddenWarningGroupsIds) {
      setHiddenWarningGroupsIds(nextHiddenWarningGroups);
    }
  }, [warningsHidden]);

  useEffect(() => {
    let nextHiddenWarningGroups = ([] as string[]).concat(
      hiddenWarningGroupsIds as string[],
    );

    if (
      !hideWarnings &&
      hideWarningsIds?.length === group?.warnings?.edges?.length
    ) {
      setHideWarnings(true);

      nextHiddenWarningGroups.push(group?.id);
    } else if (hideWarnings && hideWarningsIds?.length === 0) {
      setHideWarnings(false);

      nextHiddenWarningGroups = nextHiddenWarningGroups?.filter(
        (item) => item !== group?.id,
      );
    }

    if (setHiddenWarningGroupsIds) {
      setHiddenWarningGroupsIds(nextHiddenWarningGroups);
    }
  }, [hideWarningsIds, hideWarnings]);

  return (
    <div
      className={`${cn.warningGroupWrapper} ${cn.blacklistWarningGroupWrapper}`}
    >
      <div
        className={`${cn.warningGroupWrapperTop} ${cn.blacklistWarningGroupWrapperTop}`}
      >
        <button
          type="button"
          className={cn.warningGroupDate}
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className={cn.iconWrapper}>
            {collapsed ? <IconUp /> : <IconDown />}
          </div>
          <div
            className={`${cn.warningGroupDateValue} ${cn.blacklistWarningGroupDateValue}`}
          >
            {formatDate(group?.createdAt as string)}
          </div>
          {warningsTotal && (
            <div className={cn.totalWarningsWrapper}>
              <div className={cn.circle} />
              <div className={cn.totalWarnings}>
                {warningsTotal.unprocessed !== 0
                  ? `${warningsTotal.total - warningsTotal.unprocessed} of ${
                      warningsTotal.total
                    }`
                  : `${warningsTotal.total - warningsTotal.unprocessed} passed`}
              </div>
            </div>
          )}
        </button>
        <button
          type="button"
          className={cn.warningCollapseButton}
          onClick={() => {
            setHideWarnings(!hideWarnings);

            let nextHiddenWarningGroups = ([] as string[]).concat(
              hiddenWarningGroupsIds as string[],
            );

            if (!hideWarnings) {
              setHideWarningsIds(
                group?.warnings?.edges?.map((item) => item.node.id) as string[],
              );

              nextHiddenWarningGroups.push(group?.id as string);
            } else {
              setHideWarningsIds([]);

              nextHiddenWarningGroups = nextHiddenWarningGroups?.filter(
                (item) => item !== group?.id,
              );
            }

            if (setHiddenWarningGroupsIds) {
              setHiddenWarningGroupsIds(nextHiddenWarningGroups);
            }
          }}
        >
          <div className={cn.iconWrapper}>
            {!hideWarnings ? <IconHideAll /> : <IconExpandAll />}
          </div>
          <span>{!hideWarnings ? 'Hide All' : 'Expand All'}</span>
        </button>
      </div>

      {collapsed && (
        <div className={cn.warningWrapper}>
          {group?.warnings?.edges
            ?.filter((item) => (!showPassed ? !item.node.action : item.node))
            ?.map((warning) => (
              <BlacklistWarningItem
                key={warning.node.id}
                warning={warning.node}
                employee={currentEmployee}
                onApplyTransactionAction={(data) => {
                  applyWarning({
                    id: group.id,
                    warningId: data.warningId,
                    action: data.body.action,
                    comment: data.body.comment,
                    matchStatus: data.body.matchStatus,
                  });
                }}
                setBlacklistDataVisible={() => {
                  setBlacklistDataVisible(true);
                  setPanelIsOpen(false);
                }}
                setBlacklistItemVisible={() => {
                  setBlacklistItemVisible(true);
                  setPanelIsOpen(false);
                }}
                onResetTransactionWarningAction={(warningId) => {
                  if (onResetTransactionWarningAction) {
                    onResetTransactionWarningAction(warningId, group.id);
                  }
                }}
                applyTransactionsWarningIsLoading={applyWarningInProgress}
                setBlacklistData={(data) => setBlacklistData(data)}
                setBlacklistDataItem={(data) => setBlacklistDataItem(data)}
                activePermissions={activePermissions}
                hideApproveActions={hideApproveActions}
                hideRejectActions={hideRejectActions}
                resetTransactionsWarningIsLoading={
                  resetPaymentOrderTransactionActionIsLoading
                }
                resetTransactionsWarningIsSuccess={
                  resetPaymentOrderTransactionActionIsSuccess
                }
                warningInGroupId={selectedWarningId}
                entityId={warning?.node?.warningGroupId}
                getWarningById={getPaymentOrderWarningById}
                isWarningByIdFetching={isWarningByIdFetching}
                isWarningByIdLoading={isWarningByIdLoading}
                globalCollapsed={hideWarnings}
                setHideWarningsIds={setHideWarningsIds}
                hideWarningsIds={hideWarningsIds}
                withWhiteBackground
                isPaymentOrder
                withoutChat
              />
            ))}
          {!showPassed &&
            !group?.warnings?.edges?.filter((item) => !item.node.action)
              ?.length && (
              <div className={cn.emptyWarnings}>All passed were hidden</div>
            )}
          {getPaymentOrderWarningsAction &&
            group?.warnings?.pageInfo.hasNextPage && (
              <div className={cn.warningsGroupPagination}>
                <ButtonPseudo
                  text={
                    selectedWarningGroup === group.id && paginationInProgress
                      ? 'Loading...'
                      : 'Show more'
                  }
                  disabled={paginationInProgress}
                  onClick={() =>
                    getPaymentOrderWarningsAction({
                      warningGroupId: group.id,
                      cursor: group?.warnings?.pageInfo?.endCursor,
                    })
                  }
                  bold
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
}
